import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { films } from "@/mocks/films";

export default function HeroCarousel() {
  const featured = useMemo(() => films.filter((f) => f.backdrop), []);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || featured.length <= 1) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % featured.length), 6000);
    return () => clearInterval(timer);
  }, [paused, featured.length]);

  if (featured.length === 0) {
    return (
      <section className="w-full h-[420px] md:h-[560px] flex items-center justify-center bg-primary-900 text-background-50">
        <div className="text-center">
          <i className="ri-inbox-archive-line text-4xl"></i>
          <p className="mt-3 text-sm text-primary-200">暂无焦点推荐影视</p>
        </div>
      </section>
    );
  }

  const go = (dir: number) => setIndex((i) => (i + dir + featured.length) % featured.length);
  const current = featured[index];

  return (
    <section
      className="relative w-full h-[520px] md:h-[640px] lg:h-[720px] overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {featured.map((film, i) => (
        <div
          key={film.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100" : "opacity-0"}`}
        >
          <img
            src={film.backdrop}
            alt={`${film.title} 剧照`}
            title={`${film.title} - 欧美伦理影视`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/70 to-primary-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/40"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-8 flex items-center">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 mb-4">
            <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-accent-500 text-foreground-950">
              焦点推荐
            </span>
            <span className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-background-50/15 text-background-50 backdrop-blur-sm">
              {current.type === "movie" ? "电影" : "电视剧"}
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-extrabold text-background-50 leading-[1.1] tracking-tight">
            {current.title}
          </h1>
          <p className="mt-2 text-sm md:text-base text-primary-200/90 tracking-wide">{current.titleEn}</p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-4 text-xs md:text-sm text-background-50/85">
            <span className="flex items-center gap-1 text-accent-400 font-bold">
              <i className="ri-star-fill"></i>
              {current.rating.toFixed(1)}
            </span>
            <span>{current.year}</span>
            <span className="w-1 h-1 rounded-full bg-background-50/40"></span>
            <span>{current.region}</span>
            <span className="w-1 h-1 rounded-full bg-background-50/40"></span>
            <span>{current.genres.join(" / ")}</span>
            <span className="w-1 h-1 rounded-full bg-background-50/40"></span>
            <span>{current.duration}</span>
          </div>
          <p className="mt-5 text-sm md:text-[15px] leading-relaxed text-background-50/80 max-w-xl line-clamp-3">
            {current.synopsis}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-7">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 rounded-md bg-accent-500 text-foreground-950 text-sm md:text-[15px] font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg"></i>
              查看详情
            </Link>
            <Link
              to={`/topic/${current.topicIds[0] ?? "marriage"}`}
              className="inline-flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 rounded-md bg-background-50/10 backdrop-blur-sm text-background-50 text-sm md:text-[15px] font-medium hover:bg-background-50/20 transition-colors whitespace-nowrap"
            >
              <i className="ri-bookmark-3-line"></i>
              浏览相关专题
            </Link>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="absolute z-20 bottom-6 md:bottom-8 left-4 md:left-8 right-4 md:right-8 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {featured.map((film, i) => (
            <button
              key={film.id}
              onClick={() => setIndex(i)}
              aria-label={`切换到 ${film.title}`}
              className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                i === index ? "w-8 bg-accent-500" : "w-3 bg-background-50/40 hover:bg-background-50/70"
              }`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => go(-1)}
            aria-label="上一个"
            className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/10 backdrop-blur-sm text-background-50 hover:bg-background-50/25 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            onClick={() => go(1)}
            aria-label="下一个"
            className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/10 backdrop-blur-sm text-background-50 hover:bg-background-50/25 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>
      </div>
    </section>
  );
}